import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TopInfoTickerContent } from '@/domain';
import { cn } from '@/lib/cn';
import { useIsDesktop } from '@/lib/useMediaQuery';
import { Icon } from '@/ui/common/icon';
import { useTopInfoTicker } from './useTopInfoTicker';

/** Legacy marquee speeds (px/s): mobile 80, desktop 120. */
const MOBILE_PX_PER_SEC = 80;
const DESKTOP_PX_PER_SEC = 120;
const MIN_DURATION_SEC = 10;

/**
 * Survives AppShell remounts when switching modules (e.g. sports ↔ casino via
 * LiveFeedProvider). Cleared only on full document reload when the module resets.
 */
let topInfoTickerDismissed = false;

/**
 * Site-wide CMS news ticker (legacy `TopInfoTicker`). Dismissible until a
 * full page reload — not reset by SPA module / route changes.
 */
export function TopInfoTicker() {
  const content = useTopInfoTicker();
  const [dismissed, setDismissed] = useState(topInfoTickerDismissed);

  if (!content || dismissed) return null;

  return (
    <TopInfoTickerView
      content={content}
      onClose={() => {
        topInfoTickerDismissed = true;
        setDismissed(true);
      }}
    />
  );
}

export interface TopInfoTickerViewProps {
  content: TopInfoTickerContent;
  onClose: () => void;
}

/** Presentational ticker bar — exported for tests. */
export function TopInfoTickerView({ content, onClose }: TopInfoTickerViewProps) {
  const { t } = useTranslation();
  const isDesktop = useIsDesktop();
  const textRef = useRef<HTMLDivElement>(null);
  const [durationSec, setDurationSec] = useState(MIN_DURATION_SEC);

  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el) return;

    const update = () => {
      const distance = el.scrollWidth;
      const pxPerSec = isDesktop ? DESKTOP_PX_PER_SEC : MOBILE_PX_PER_SEC;
      setDurationSec(Math.max(distance / pxPerSec, MIN_DURATION_SEC));
    };

    update();

    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    observer?.observe(el);
    return () => observer?.disconnect();
  }, [content.htmlText, isDesktop]);

  return (
    <div
      className={cn(
        'relative overflow-hidden px-2 py-1.5 pr-10 text-center text-xs',
        'bg-bg-elevated text-text-primary',
      )}
      role="region"
      aria-label={t('News', { defaultValue: 'News' })}
    >
      <div
        ref={textRef}
        className={cn(
          'inline-block max-w-none box-content',
          'motion-safe:animate-top-info-ticker motion-safe:whitespace-nowrap motion-safe:pl-[100%]',
          'motion-reduce:max-w-full motion-reduce:whitespace-normal',
          '[&_p]:m-0 [&_p]:inline-block [&_p]:p-0 [&_p:empty]:hidden',
        )}
        style={{ animationDuration: `${durationSec}s` }}
        dangerouslySetInnerHTML={{ __html: content.htmlText }}
      />
      <button
        type="button"
        onClick={onClose}
        aria-label={t('Close', { defaultValue: 'Close' })}
        className={cn(
          'absolute top-0 right-2 bottom-0 inline-flex items-center justify-center',
          'text-icon-default hover:text-text-primary transition-colors',
        )}
      >
        <Icon name="close" size={14} />
      </button>
    </div>
  );
}
